import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Fingerprint, Settings, CreditCard,
  ArrowLeft, ShieldHalf, Wrench, Headset, Building2, ClipboardCheck, ChevronRight, Sparkles,
} from "lucide-react";
import { LOGO, api } from "../lib/api";
import { InstallButton } from "./InstallButton";
import { ProgereDownloadAppButton } from "./ProgereDownloadAppButton";
import { PUNCH_MENU, allowed } from "../config/navItems";
import { PROGERE_MODULE } from "../config/progereModule";

const CORE_URL = process.env.REACT_APP_CORE_URL || "https://progere.ca";

export default function Sidebar({ open, onClose, approveCount, user }) {
  const { t } = useTranslation();
  const [errCount, setErrCount] = useState(0);
  const [crCount, setCrCount] = useState(0);
  const [appVer, setAppVer] = useState(null);
  const role = user?.role;
  const isTech = user?.technician && !user?.platform_admin;
  const isMember = user?.platform_admin || user?.technician;

  useEffect(() => {
    if (!isMember) return;
    let alive = true;
    const fetchCount = () => api.techOverview().then((d) => alive && setErrCount((d.errors_unresolved || 0) + (d.chats_waiting || 0))).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [isMember]);

  useEffect(() => {
    let alive = true;
    api.appVersion().then((d) => alive && setAppVer(d?.version)).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (role !== "admin") return;
    let alive = true;
    const fetchCr = () => api.changeRequestsPending().then((d) => alive && setCrCount(d.count || 0)).catch(() => {});
    fetchCr();
    const t = setInterval(fetchCr, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [role]);

  return (
    <aside className={`sidebar ${open ? "open" : ""}`} data-testid="sidebar">
      <div className="brand-row">
        <div className="logo-mark"><img src={LOGO} alt="" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("span"), { textContent: "P", style: "color:var(--gold);font-weight:900;font-size:22px" })); }} /></div>
        <div>
          <div className="brand-title">
            <span className="pro">PRO</span><span className="gere">GÈRE</span> <span className="punch">{PROGERE_MODULE.name}</span>
          </div>
          <div className="brand-sub">{PROGERE_MODULE.subtitle}</div>
        </div>
      </div>

      <div className="side-section-label">{t("nav.section")}</div>
      {!isTech && PUNCH_MENU.filter((m) => allowed(m.roles, role)).map((m) => {
        const Icon = m.icon;
        return (
          <NavLink key={m.to} to={m.to} end={m.end} onClick={onClose}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}${m.hideMobile ? " nav-hide-mobile" : ""}`}
            data-testid={`punch-nav-${m.tid}`}>
            <Icon size={18} className="nav-ico" style={{ color: m.color === "red" ? "var(--red)" : "var(--gold)" }} />
            <span>{t(m.tkey)}</span>
            {m.badge && approveCount > 0 && <span className="nav-badge">{approveCount}</span>}
            <ChevronRight size={15} className="nav-chev" />
          </NavLink>
        );
      })}
      {isTech && (
        <NavLink to="/maintenance" onClick={onClose} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid="nav-maintenance-tech">
          <Wrench size={18} className="nav-ico" /> <span>{t("nav.maintenanceTech")}</span>
          {errCount > 0 && <span className="nav-badge" data-testid="maintenance-error-badge" style={{ background: "#d00000" }}>{errCount}</span>}
        </NavLink>
      )}

      <div style={{ flex: 1 }} />

      {/* Écosystème — lien de retour vers PROGÈRE CORE (admin only) */}
      {role === "admin" && (
        <>
          <div className="side-section-label">{t("nav.ecosystem")}</div>
          <a href={CORE_URL} className="nav-item" data-testid="nav-back-to-core"
            style={{ textDecoration: "none" }} title="Retour à PROGÈRE CORE">
            <ArrowLeft size={18} className="nav-ico" color="var(--gold)" />
            <span>{t("nav.backToCore")}</span>
          </a>
        </>
      )}

      <div style={{ height: 8 }} />
      {user?.platform_admin && (
        <NavLink to="/console" onClick={onClose} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid="nav-console">
          <ShieldHalf size={18} className="nav-ico" /> <span>{t("nav.console")}</span>
        </NavLink>
      )}
      {user?.platform_admin && (
        <NavLink to="/maintenance" onClick={onClose} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid="nav-maintenance">
          <Wrench size={18} className="nav-ico" /> <span>{t("nav.maintenance")}</span>
          {errCount > 0 && <span className="nav-badge" data-testid="maintenance-error-badge" style={{ background: "#d00000" }}>{errCount}</span>}
        </NavLink>
      )}
      {user?.platform_admin && (
        <NavLink to="/entreprises" onClick={onClose} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid="nav-entreprises">
          <Building2 size={18} className="nav-ico" /> <span>{t("nav.companies")}</span>
        </NavLink>
      )}
      {role === "admin" && (
        <NavLink to="/demandes-comptable" onClick={onClose} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid="nav-demandes-comptable">
          <ClipboardCheck size={18} className="nav-ico" /> <span>{t("nav.accountantRequests")}</span>
          {crCount > 0 && <span className="nav-badge" data-testid="cr-pending-badge">{crCount}</span>}
        </NavLink>
      )}
      {role === "admin" && (
        <NavLink to="/parametres" onClick={onClose} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid="nav-parametres">
          <Settings size={18} className="nav-ico" /> <span>{t("nav.settings")}</span>
        </NavLink>
      )}
      {role === "admin" && (
        <NavLink to="/abonnement" onClick={onClose} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid="nav-abonnement">
          <CreditCard size={18} className="nav-ico" /> <span>{t("nav.subscription")}</span>
        </NavLink>
      )}
      <div style={{ height: 8 }} />
      {!isTech && (
        <button className="nav-item" style={{ width: "100%", textAlign: "left", cursor: "pointer", background: "none" }}
          onClick={() => { window.dispatchEvent(new CustomEvent("pp:open-livechat")); onClose && onClose(); }} data-testid="nav-livechat">
          <Headset size={18} className="nav-ico" /> <span>{t("nav.liveSupport")}</span>
        </button>
      )}
      <div style={{ height: 14 }} />
      <div style={{ padding: "0 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        <ProgereDownloadAppButton variant="sidebar" size="sm" moduleCode="punch" />
        <InstallButton className="btn btn-outline btn-sm" label={t("nav.installApp")} size={15} />
      </div>
      <div style={{ height: 18 }} />
      <div style={{ marginTop: "auto", padding: "12px 16px 18px", borderTop: "1px solid var(--border-soft)" }} data-testid="brand-credit">
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--gold)", letterSpacing: ".3px" }}>PROGÈRE {PROGERE_MODULE.name}</div>
        <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 2 }}>{t("nav.poweredBy")}</div>
        {appVer && (
          <NavLink to="/nouveautes" onClick={onClose} data-testid="sidebar-version-badge"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 10.5, fontWeight: 700,
              color: "var(--text)", textDecoration: "none", padding: "3px 8px", borderRadius: 20,
              border: "1px solid var(--border-gold)", background: "rgba(var(--gold-rgb),.08)" }}>
            <Sparkles size={11} style={{ color: "var(--gold)" }} /> Version {appVer}
          </NavLink>
        )}
      </div>
    </aside>
  );
}
